import { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Divider,
  Button,
  Toolbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate, useParams, Link } from 'react-router';
import { ArrowBack } from '@mui/icons-material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { LoadingButton } from '@mui/lab';
import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';

import { useApi } from '@/providers/ApiContext';
import {
  getManifest,
  deleteManifest,
  approveManifest,
  rejectManifest,
  submitManifest,
  generateManifestChecks,
} from '@/actions/manifest';
import ManifestDeleteDialog from '../ManifestDeleteDialog';
import toNiceTimestamp from '@/util/toNiceTimestamp';
import { useDocTypes } from '@/providers/DocTypesContext';
import AdminManifestChecks from './AdminManifestChecks';
import type { Manifest, EntityId } from '@/types';

function AdminManifest(): React.JSX.Element {
  const user = useUser();
  const api = useApi();
  const navigate = useNavigate();
  const doc_types = useDocTypes();
  const { manifest_id } = useParams<{ manifest_id: string }>();
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [loading_manifest, setLoadingManifest] = useState<boolean>(true);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [no_checks, setNoChecks] = useState<boolean>(true);
  const [api_error, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.entity_id && !manifest && manifest_id && api) {
      getManifest(api, manifest_id)
        .then((data) => {
          setManifest(data);
          setLoadingManifest(false);
        })
        .catch((error) => {
          console.log(error);
          navigate('/error', {
            state: {
              errorMessage: 'Failed to load manifest',
              errorDetails:
                (error as Error).message ||
                'Unable to retrieve the requested manifest. This could be due to network issues, server problems, or the manifest may no longer exist.',
              returnPath: `/admin/manifests/${manifest_id}`,
            },
          });
        });
    }
  }, [api, user?.entity_id, manifest, manifest_id, navigate]);

  const handleDeleteManifest = async (manifest_id: EntityId): Promise<void> => {
    if (!api) return;
    try {
      await deleteManifest(api, manifest_id);
      if (manifest && doc_types) {
        const doc_type = doc_types.find(
          (doc_type) => doc_type.entity_id === manifest.document_type_id,
        );
        if (doc_type) {
          navigate({
            pathname: '/admin/manifests',
            search: `?type=${doc_type.code}`,
          });
        }
      }
    } catch (error) {
      console.error('Error deleting manifest:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to delete the manifest. This could be due to network issues, server problems, or insufficient permissions.',
      );
    }
  };

  const handleApprove = async (): Promise<void> => {
    if (!manifest) return;
    if (!api) return;
    try {
      setActionLoading(true);
      await approveManifest(api, manifest.entity_id);
      // Refresh the manifest data
      const updatedManifest = await getManifest(api, manifest.entity_id);
      setManifest(updatedManifest);
    } catch (error) {
      console.error('Error approving manifest:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to approve the manifest. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (): Promise<void> => {
    if (!manifest) return;
    if (!api) return;
    try {
      setActionLoading(true);
      await rejectManifest(api, manifest.entity_id);
      // Refresh the manifest data
      const updatedManifest = await getManifest(api, manifest.entity_id);
      setManifest(updatedManifest);
    } catch (error) {
      console.error('Error rejecting manifest:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to reject the manifest. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!manifest) return;
    if (!api) return;
    try {
      setSubmitting(true);
      await submitManifest(api, manifest.entity_id);
      // Refresh the manifest data
      const updatedManifest = await getManifest(api, manifest.entity_id);
      setManifest(updatedManifest);
    } catch (error) {
      console.error('Error submitting manifest:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to submit the manifest. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading_manifest || !manifest) {
    return <LoadingMask />;
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant='h6' sx={{ mr: 4 }}>
          {manifest.name} Manifest
        </Typography>
        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() => {
              navigate({
                pathname: '/admin/manifests/edit',
                search: `?manifest_id=${manifest.entity_id}`,
              });
            }}
            startIcon={<EditOutlinedIcon />}
          >
            Edit
          </Button>
          <Button
            onClick={() => {
              navigate({
                pathname: `/admin/manifests/${manifest.entity_id}/split`,
              });
            }}
            startIcon={<CallSplitOutlinedIcon />}
          >
            Split
          </Button>
          <Button
            onClick={() => setDeleteOpen(true)}
            startIcon={<DeleteOutlinedIcon />}
          >
            Delete
          </Button>
        </Stack>
      </Toolbar>
      <Divider />

      <Box padding='15px'>
        <Typography variant='subtitle1'>Basic Information</Typography>
        <Divider />
        <br />
        <Grid
          container
          component='dl' // mount a Definition List
          spacing={0}
          sx={{ display: 'table' }}
        >
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>ID:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {manifest.entity_id}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Status:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {manifest.status}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Created:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {toNiceTimestamp(manifest.created_at, true)}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Reference Document:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {(
                manifest as Manifest & {
                  reference_document?: {
                    entity_id: string;
                    doc_id?: string;
                    name: string;
                  };
                }
              )?.reference_document ? (
                <Link
                  to={`/admin/documents/${(manifest as Manifest & { reference_document?: { entity_id: string } }).reference_document?.entity_id}`}
                  style={{ textDecoration: 'underline', color: '#1976d2' }}
                >
                  {
                    (
                      manifest as Manifest & {
                        reference_document?: { doc_id?: string; name: string };
                      }
                    ).reference_document?.doc_id
                  }{' '}
                  -{' '}
                  {
                    (
                      manifest as Manifest & {
                        reference_document?: { name: string };
                      }
                    ).reference_document?.name
                  }
                </Link>
              ) : null}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {manifest.status === 'pending' && (
        <Box padding='15px'>
          <Typography variant='subtitle1'>Actions</Typography>
          <Divider />
          <br />
          <Stack direction='row' spacing={2}>
            <LoadingButton
              variant='contained'
              color='success'
              loading={actionLoading}
              onClick={handleApprove}
              disabled={actionLoading}
              startIcon={<CheckIcon />}
            >
              Approve
            </LoadingButton>
            <LoadingButton
              variant='contained'
              color='error'
              loading={actionLoading}
              onClick={handleReject}
              disabled={actionLoading}
              startIcon={<CloseIcon />}
            >
              Reject
            </LoadingButton>
          </Stack>
        </Box>
      )}

      {manifest.status === 'draft' ? (
        <Box padding='15px'>
          <Stack direction='row' spacing={2}>
            <Tooltip
              title={
                !(manifest as Manifest & { reference_document?: unknown })
                  ?.reference_document
                  ? 'No configured reference document'
                  : ''
              }
            >
              <span>
                <LoadingButton
                  loading={generating}
                  onClick={async () => {
                    if (!manifest) return;
                    try {
                      setGenerating(true);
                      if (api) {
                        await generateManifestChecks(api, manifest.entity_id);
                      }
                      setGenerating(false);
                      if (doc_types) {
                        const doc_type = doc_types.find(
                          (doc_type) =>
                            doc_type.entity_id === manifest.document_type_id,
                        );
                        if (doc_type) {
                          navigate({
                            pathname: '/admin/manifests',
                            search: `?type=${doc_type.code}`,
                          });
                        }
                      }
                    } catch (error) {
                      console.error('Error generating manifest checks:', error);
                      setGenerating(false);
                      const err = error as Error;
                      setApiError(
                        err.message ||
                          'Unable to generate checks for the manifest. This could be due to network issues, server problems, or the manifest may be in an invalid state.',
                      );
                    }
                  }}
                  startIcon={<BuildOutlinedIcon />}
                  variant='contained'
                  disabled={
                    !(manifest as Manifest & { reference_document?: unknown })
                      ?.reference_document || generating
                  }
                >
                  Generate Checks
                </LoadingButton>
              </span>
            </Tooltip>
            <LoadingButton
              variant='outlined'
              loading={submitting}
              onClick={handleSubmit}
              disabled={no_checks || submitting}
              startIcon={<SendOutlinedIcon />}
            >
              Submit For Approval
            </LoadingButton>
          </Stack>
        </Box>
      ) : undefined}

      <Box padding='15px'>
        <Typography variant='subtitle1'>Checks</Typography>
        <AdminManifestChecks manifest={manifest} setEmpty={setNoChecks} />
      </Box>

      <ManifestDeleteDialog
        manifest_id={manifest.entity_id}
        deleteManifest={handleDeleteManifest}
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
      />

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default AdminManifest;
